'use client'

import { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { runWhenPageVisible } from '@/lib/analytics'

export type FlagVariant = string | boolean | undefined

function readFlagSilently(flag: string): FlagVariant {
  try {
    return posthog.getFeatureFlag(flag, { send_event: false })
  } catch {
    return undefined
  }
}

/**
 * Read a feature flag the way `useFeatureFlagVariantKey` from posthog-js/react
 * does (initial read plus a re-read whenever flags load or reload), but WITHOUT
 * the `$feature_flag_called` event. That hook emits the exposure on the first
 * read, which happens at mount, so every reader of a page that merely mounts
 * the component counts as exposed: hidden and prerendered tabs, readers who
 * never scroll to or open the surface, and viewports where it is hidden.
 *
 * Pair every use with `useFlagExposure` at the point the surface is actually
 * shown to the reader. PostHog experiments read exposure from
 * `$feature_flag_called`, so a flag read here without that pairing silently
 * drops out of its experiment.
 */
export function useSilentFlagVariant(flag: string): FlagVariant {
  const [variant, setVariant] = useState<FlagVariant>(() => readFlagSilently(flag))

  useEffect(() => {
    setVariant(readFlagSilently(flag))
    try {
      return posthog.onFeatureFlags(() => setVariant(readFlagSilently(flag)))
    } catch {
      return undefined
    }
  }, [flag])

  return variant
}

/**
 * Send the experiment exposure for `flag` once per mount, when `shouldExpose`
 * is true and the page is visible to the reader.
 *
 * The event is posthog-js's own `$feature_flag_called`, produced by a plain
 * `getFeatureFlag(flag)` call, so its properties (`$feature_flag_response`,
 * `$feature_flag_id`, version, reason, request id) and its per-value dedup are
 * exactly what the SDK hooks send. Only the moment changes. Callers must pass
 * `shouldExpose = false` until the flag has resolved: a read before flags load
 * returns undefined and sends nothing, which would spend this mount's one shot.
 */
export function useFlagExposure(flag: string, shouldExpose: boolean): void {
  const sentRef = useRef(false)

  useEffect(() => {
    if (!shouldExpose || sentRef.current) return undefined
    return runWhenPageVisible(() => {
      if (sentRef.current) return
      sentRef.current = true
      try {
        posthog.getFeatureFlag(flag)
      } catch {
        // Exposure is analytics only; never break the surface over it.
      }
    })
  }, [flag, shouldExpose])
}
